'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import { Users, Search, AlertTriangle, CheckCircle, Clock, Eye } from 'lucide-react';

const TAXPAYERS = [
  { id: 'TIN-2024-001847', name: 'Kollie Trading Co.', type: 'Corporate', county: 'Montserrado', riskScore: 22, complianceScore: 91, status: 'compliant', lastFiling: '2025-03-15', outstanding: '$0', sector: 'Commerce' },
  { id: 'TIN-2024-003291', name: 'Mary Pewee', type: 'Individual', county: 'Montserrado', riskScore: 67, complianceScore: 54, status: 'at-risk', lastFiling: '2024-11-02', outstanding: '$4,200', sector: 'Services' },
  { id: 'TIN-2024-007812', name: 'Bong County Dev. Corp.', type: 'Corporate', county: 'Bong', riskScore: 15, complianceScore: 96, status: 'compliant', lastFiling: '2025-04-01', outstanding: '$0', sector: 'Development' },
  { id: 'TIN-2024-009034', name: 'LPA Holdings Ltd.', type: 'Corporate', county: 'Grand Bassa', riskScore: 41, complianceScore: 73, status: 'review', lastFiling: '2025-01-20', outstanding: '$12,800', sector: 'Logistics' },
  { id: 'TIN-2024-011205', name: 'Maryland Fisheries Ltd.', type: 'Corporate', county: 'Maryland', riskScore: 28, complianceScore: 88, status: 'compliant', lastFiling: '2025-03-28', outstanding: '$0', sector: 'Fisheries' },
  { id: 'TIN-2024-014567', name: 'James Kollie', type: 'Individual', county: 'Nimba', riskScore: 82, complianceScore: 31, status: 'delinquent', lastFiling: '2023-09-14', outstanding: '$28,500', sector: 'Real Estate' },
  { id: 'TIN-2024-018923', name: 'Gedeh Commerce Ltd.', type: 'Corporate', county: 'Grand Gedeh', riskScore: 19, complianceScore: 94, status: 'compliant', lastFiling: '2025-04-10', outstanding: '$0', sector: 'Commerce' },
  { id: 'TIN-2024-022341', name: 'Sinoe Timber Co.', type: 'Corporate', county: 'Sinoe', riskScore: 74, complianceScore: 42, status: 'at-risk', lastFiling: '2024-08-30', outstanding: '$67,200', sector: 'Forestry' },
  { id: 'TIN-2024-025678', name: 'Fatima Kamara', type: 'Individual', county: 'Lofa', riskScore: 35, complianceScore: 79, status: 'review', lastFiling: '2025-02-14', outstanding: '$1,900', sector: 'Agriculture' },
  { id: 'TIN-2024-031002', name: 'Margibi Industrial Corp.', type: 'Corporate', county: 'Margibi', riskScore: 58, complianceScore: 61, status: 'at-risk', lastFiling: '2024-12-05', outstanding: '$34,100', sector: 'Manufacturing' },
];

const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
  compliant: { color: '#34d399', bg: 'rgba(52,211,153,0.12)', label: 'Compliant' },
  'at-risk': { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)', label: 'At Risk' },
  review: { color: '#60a5fa', bg: 'rgba(96,165,250,0.12)', label: 'Under Review' },
  delinquent: { color: '#f87171', bg: 'rgba(248,113,113,0.12)', label: 'Delinquent' },
};

const kpis = [
  { label: 'Total Registered', value: '48,291', icon: Users, color: 'var(--accent)' },
  { label: 'Compliant (90d)', value: '71.4%', icon: CheckCircle, color: '#34d399' },
  { label: 'High Risk', value: '3,847', icon: AlertTriangle, color: '#f87171' },
  { label: 'Pending Review', value: '1,203', icon: Clock, color: '#fbbf24' },
];

export default function TaxpayerRegistryClient() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = TAXPAYERS.filter((t) => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.id.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All' || t.status === statusFilter.toLowerCase().replace(' ', '-');
    const matchType = typeFilter === 'All' || t.type === typeFilter;
    return matchSearch && matchStatus && matchType;
  });

  const selectedTaxpayer = TAXPAYERS.find((t) => t.id === selectedId);

  return (
    <AppLayout>
      <div className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}>
              <Users size={20} style={{ color: '#070e1f' }} />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Taxpayer Registry</h1>
              <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>Unified taxpayer identity · Risk scoring · Compliance profiles — all 15 counties</p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
            <AlertTriangle size={12} />
            3,847 High Risk
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {kpis.map((k) => {
            const KIcon = k.icon;
            return (
              <div key={k.label} className="rounded-xl px-4 py-4 flex items-center gap-3"
                style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${k.color}18` }}>
                  <KIcon size={18} style={{ color: k.color }} />
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{k.label}</p>
                  <p className="text-lg font-bold font-mono-data" style={{ color: k.color }}>{k.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Table */}
          <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            {/* Filters */}
            <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: 'var(--dark-border)' }}>
              <div className="relative flex-1 min-w-48">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input type="text" placeholder="Search name or TIN…" value={search} onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 rounded-md text-xs outline-none"
                  style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)' }} />
              </div>
              {['All', 'Compliant', 'At Risk', 'Delinquent', 'Review'].map((s) => (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                  style={{
                    backgroundColor: statusFilter === s ? 'rgba(212,160,23,0.15)' : 'var(--dark-elevated)',
                    color: statusFilter === s ? 'var(--accent)' : 'var(--dark-muted)',
                    border: `1px solid ${statusFilter === s ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
                  }}>
                  {s}
                </button>
              ))}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                    {['TIN', 'Name', 'Type', 'County', 'Risk', 'Compliance', 'Outstanding', 'Status'].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((t) => {
                    const sc = statusConfig[t.status];
                    const isSelected = t.id === selectedId;
                    return (
                      <tr key={t.id} onClick={() => setSelectedId(t.id === selectedId ? null : t.id)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid var(--dark-border)',
                          backgroundColor: isSelected ? 'rgba(212,160,23,0.06)' : 'transparent',
                        }}
                        onMouseEnter={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                        onMouseLeave={(e) => { if (!isSelected) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                      >
                        <td className="px-4 py-3 font-mono-data font-semibold" style={{ color: 'var(--accent)' }}>{t.id}</td>
                        <td className="px-4 py-3 font-medium" style={{ color: 'var(--dark-text)' }}>{t.name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{t.type}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--dark-muted)' }}>{t.county}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                              <div className="h-full rounded-full" style={{ width: `${t.riskScore}%`, backgroundColor: t.riskScore > 60 ? '#f87171' : t.riskScore > 35 ? '#fbbf24' : '#34d399' }} />
                            </div>
                            <span style={{ color: t.riskScore > 60 ? '#f87171' : t.riskScore > 35 ? '#fbbf24' : '#34d399' }}>{t.riskScore}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span style={{ color: t.complianceScore > 75 ? '#34d399' : t.complianceScore > 50 ? '#fbbf24' : '#f87171' }}>{t.complianceScore}%</span>
                        </td>
                        <td className="px-4 py-3 font-mono-data" style={{ color: t.outstanding === '$0' ? '#34d399' : '#f87171' }}>{t.outstanding}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Profile panel */}
          <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
            {selectedTaxpayer ? (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0"
                    style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
                    {selectedTaxpayer.name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-bold" style={{ color: 'var(--dark-text)' }}>{selectedTaxpayer.name}</p>
                    <p className="text-xs font-mono-data" style={{ color: 'var(--accent)' }}>{selectedTaxpayer.id}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Type', value: selectedTaxpayer.type },
                    { label: 'County', value: selectedTaxpayer.county },
                    { label: 'Sector', value: selectedTaxpayer.sector },
                    { label: 'Last Filing', value: selectedTaxpayer.lastFiling },
                    { label: 'Outstanding', value: selectedTaxpayer.outstanding },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'var(--dark-border)' }}>
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{row.label}</span>
                      <span className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{row.value}</span>
                    </div>
                  ))}
                  <div className="pt-2">
                    <p className="text-xs mb-2" style={{ color: 'var(--dark-muted)' }}>Risk Score</p>
                    <div className="w-full h-2 rounded-full overflow-hidden mb-1" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${selectedTaxpayer.riskScore}%`, backgroundColor: selectedTaxpayer.riskScore > 60 ? '#f87171' : selectedTaxpayer.riskScore > 35 ? '#fbbf24' : '#34d399' }} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Low</span>
                      <span className="text-xs font-bold" style={{ color: selectedTaxpayer.riskScore > 60 ? '#f87171' : selectedTaxpayer.riskScore > 35 ? '#fbbf24' : '#34d399' }}>{selectedTaxpayer.riskScore}/100</span>
                      <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>High</span>
                    </div>
                  </div>
                  <div className="pt-2">
                    <p className="text-xs mb-2" style={{ color: 'var(--dark-muted)' }}>Compliance Score</p>
                    <div className="w-full h-2 rounded-full overflow-hidden mb-1" style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      <div className="h-full rounded-full" style={{ width: `${selectedTaxpayer.complianceScore}%`, backgroundColor: selectedTaxpayer.complianceScore > 75 ? '#34d399' : selectedTaxpayer.complianceScore > 50 ? '#fbbf24' : '#f87171' }} />
                    </div>
                    <p className="text-xs text-right font-bold" style={{ color: selectedTaxpayer.complianceScore > 75 ? '#34d399' : '#fbbf24' }}>{selectedTaxpayer.complianceScore}%</p>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
                      View Full Profile
                    </button>
                    <button className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{ backgroundColor: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
                      Flag for Audit
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <Eye size={32} className="mb-3" style={{ color: 'var(--dark-muted)' }} />
                <p className="text-sm font-medium" style={{ color: 'var(--dark-muted)' }}>Select a taxpayer</p>
                <p className="text-xs mt-1" style={{ color: 'var(--dark-muted)', opacity: 0.6 }}>Click any row to view full profile and risk details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
